import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"
import validationService from "data/validate"

type Attribute = {
  attributeid: number
}
// private module logic //

const id: string = "attributes"
const initialState: ApiResult<Attribute[]> = { id, state: "init", value: [] }
const attributeStream = new BehaviorSubject<ApiResult<Attribute[]>>(initialState)
const attributesEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

let attributeMap = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  attributeStream.next({ id, state: 'ok', value: [] })
  attributeMap = {}
}

function get(attributeid: number): Attribute {
  return attributeMap[attributeid]
}

function refresh(): Promise<any> {
  return fetchAttributes()
}

function fetchAttributes(
  limit: number = 0,
  offset: number = 0
): Promise<Attribute[] | { message: string }> {

  clear()
  attributeStream.next({ id, state: 'busy', value: attributeStream.getValue().value })

  let url = `attribute?order=sortorder.asc`
  if (limit && offset >= 0) {
    url += `&limit=${limit}&offset=${offset}`
  }

  return _fetches_.watch('attributes', Client.get(url)
  .then((records: Attribute[]) => {
    _.forEach(records, (attribute) => (attributeMap[attribute.attributeid] = attribute))
    // merge results unique by attributeid
    attributeStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(): Promise<Attribute | { message: string }> {
  const currentValue = attributeStream.getValue().value
  attributeStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    sortorder: (_(currentValue).map('sortorder').min() - 1) || 0
  }

  return Client.post(`attribute`, payload)
    .then((attribute: Attribute) => {
      push_undo({
        undofunc: () => deleteOne(attribute.attributeid),
        redofunc: () => create()
      })
      attributeMap[attribute.attributeid] = attribute
      // merge results unique by attributeid
      const currentValue = attributeStream.getValue().value
      attributeStream.next({
        id,
        state: 'ok',
        value: _.unionBy([attribute], currentValue, 'attributeid')
      })
      attributesEventStream.next({ type: 'ATTRIBUTE_CREATED', payload: attribute })
      validationService.fetch('attribute', attribute.attributeid)
      return attribute
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      attributeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchAttributes()
      return err
    })
}

function copy(attributeid:number, code:string) {
  const currentValue = attributeStream.getValue().value;
  attributeStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'attribute', recordid: attributeid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((attributes: Attribute[]) => {
      const newRecord = attributes[0]
      push_undo({
        undofunc: () => deleteOne(newRecord.attributeid),
        redofunc: () => copy(attributeid, code)
      });
      attributeMap[newRecord.attributeid] = newRecord
      // merge results unique by attributeid
      const currentValue = attributeStream.getValue().value
      attributeStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'attributeid').sort((a, b) => alphanum(a.code, b.code))
      })
      attributesEventStream.next({ type: 'ATTRIBUTE_COPIED', payload: newRecord })
      validationService.fetchAll('attribute')
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      attributeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchAttributes()
      return err
    })
}

function copies(attributeid:number, count: number) {
  const currentValue = attributeStream.getValue().value;
  attributeStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'attribute', recordid: attributeid, _synchronous: true, unique: true, count};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((attributes: Attribute[]) => {
      push_undo({
        undofunc: () => deleteMultiple(_.map(attributes, 'attributeid')),
        redofunc: () => copies(attributeid, count)
      });
      _.forEach(attributes, newRecord => attributeMap[newRecord.attributeid] = newRecord)
      // merge results unique by attributeid
      const currentValue = attributeStream.getValue().value
      attributeStream.next({
        id,
        state: 'ok',
        value: _.unionBy(attributes, currentValue, 'attributeid').sort((a, b) => alphanum(a.code, b.code))
      })
      attributesEventStream.next({ type: 'ATTRIBUTE_COPIED', payload: attributes })
      validationService.fetchAll('attribute')
      return attributes
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      attributeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchAttributes()
      return err
    })
}

async function deleteOne(
  attributeid: number
): Promise<boolean | { message: string }> {
  const currentValue = attributeStream.getValue().value

  const deleteFn = () => deleteOne(attributeid)
  const refreshFn = () => fetchAttributes()
  if (!(await handleReferences('attribute', attributeid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted attributeid
  const value = _(currentValue)
    .filter((p) => p.attributeid !== attributeid)
    .value()
  value.forEach((p) => (attributeMap[p.attributeid] = p))

  attributeStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(attributeid).then(() =>
  Client.del(`attribute?attributeid=eq.${attributeid}`)
    .then((attributes: Attribute[]) => {
      attributes.forEach(a => delete attributeMap[a.attributeid])
      push_undo({
        undofunc: () => {
          Client.post('attribute', attributes)
          attributes.forEach(a => attributeMap[a.attributeid] = a)
          attributeStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(attributeid)
      })
      validationService.fetchAll('attribute')
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      attributeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchAttributes()
      return err
    })
  )
}

async function deleteMultiple(
  attributeids: number[]
): Promise<boolean | { message: string }> {
  const currentValue = attributeStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(ids)
  const refreshFn = () => fetchAttributes()
  if (!(await handleReferences('attribute', attributeids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted attributeid
  const value = _(currentValue)
    .filter((p) => !attributeids.includes(p.attributeid))
    .value()
  value.forEach((p) => (attributeMap[p.attributeid] = p))

  attributeStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(attributeids).then(() =>
  Client.del(`attribute?attributeid=in.${attributeids}`)
    .then((attributes: Attribute[]) => {
      attributes.forEach(a => delete attributeMap[a.attributeid])
      push_undo({
        undofunc: () => {
          Client.post('attribute', attributes)
          attributes.forEach(a => attributeMap[a.attributeid] = a)
          attributeStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteMultiple(attributeids)
      })
      validationService.fetchAll('attribute')
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      attributeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchAttributes()
      return err
    })
  )
}

function patch(attribute: {
  attributeid: number
}): Promise<Attribute | { message: string }> {
  const currentValue = attributeStream.getValue().value
  const attributeid = attribute.attributeid
  const currentattributes = _.find(currentValue, { attributeid })
  const changeset = diff_obj(attribute, currentattributes)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentattributes),
    redofunc: () => patch(attribute)
  })

  // optimistic update
  attributeStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {attributeid}), attribute)
  })

  return _patches_.watch(attributeid, Client.patch(`attribute?attributeid=eq.${attributeid}`, changeset)
    .then((attributes: Attribute[]) => {
      _.forEach(attributes, (attribute) => (attributeMap[attribute.attributeid] = attribute))
      validationService.fetchAll('attribute')
      return attributes[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      attributeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchAttributes()
      return err
    }))
}

function move(
  attributeid: number,
  destination: number
): Promise<boolean | { message: string }> {
  if (!_.isFinite(destination)) return Promise.resolve(false)

  const currentValue = attributeStream.getValue().value

  let undo
  const orders = (() => {
    let orders = _(currentValue).orderBy('sortorder').map('attributeid').value()
    const current = _.findIndex(orders, (el) => el == attributeid)
    undo = {
      undofunc: () => move(attributeid, current + 1),
      redofunc: () => move(attributeid, destination)
    }

    const dest = destination - 1
    if (dest < current) {
      orders.splice(dest, 0, attributeid)
      orders.splice(current + 1, 1)
    } else {
      orders.splice(dest + 1, 0, attributeid)
      orders.splice(current, 1)
    }
    return orders
  })()
  const value = _.map(orders, (id, ix) => {
    attributeMap[id] = { ...attributeMap[id], sortorder: ix + 1 }
    return attributeMap[id]
  })
  attributeStream.next({ id, state: 'ok', value })

  const payload = {
    table: 'attribute',
    organizationid: currentValue['organizationid'],
    orders,
    _synchronous: true
  }

  // update the db
  return Client.post(`msg/svc_reorder`, payload)
    .then((_attributes: Attribute[]) => {
      // update already happened optimistically
      push_undo(undo)
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      attributeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchAttributes()
      return err
    })
}

// public module interface //

const attributesService = {
  stream: () => attributeStream.asObservable(),
  value: () => attributeStream.getValue().value,
  eventStream: () => attributesEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  deleteMultiple,
  get,
  fetchAttributes,
  patch,
  refresh,
  move
}

export default attributesService

